import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { ExternalLink, Copy, Check } from 'lucide-react';
import { Button } from './ui/button';
import { useState } from 'react';

const FacebookImportGuide = ({ open, onOpenChange }) => {
  const [copied, setCopied] = useState(false);

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">How to Import Photos from Facebook</DialogTitle>
          <DialogDescription>
            Follow these steps to get direct links to your Facebook photos
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Method 1 */}
          <div className="border rounded-lg p-4 bg-blue-50">
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
              From Facebook Photo Page
            </h3>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-2">
                <span className="font-semibold min-w-[20px]">1.</span>
                <span>Go to your Facebook profile: <a href="https://www.facebook.com/daniel.alich.1" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                  https://www.facebook.com/daniel.alich.1 <ExternalLink className="w-3 h-3" />
                </a></span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold min-w-[20px]">2.</span>
                <span>Click on "Photos" tab to view your photos</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold min-w-[20px]">3.</span>
                <span>Click on any photo to open it in full view</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold min-w-[20px]">4.</span>
                <span>Right-click on the photo and select "Open image in new tab"</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold min-w-[20px]">5.</span>
                <span>Copy the URL from the address bar (it should start with <code className="bg-gray-100 px-1 rounded">https://scontent</code>)</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold min-w-[20px]">6.</span>
                <span>Paste this URL into the "Import from URL" dialog in the album</span>
              </li>
            </ol>
          </div>

          {/* Method 2 */}
          <div className="border rounded-lg p-4 bg-green-50">
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <span className="bg-green-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
              Download Facebook Data (Bulk Import)
            </h3>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-2">
                <span className="font-semibold min-w-[20px]">1.</span>
                <span>Go to Facebook Settings → Your Facebook Information → Download Your Information</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold min-w-[20px]">2.</span>
                <span>Select "Photos and Videos" only</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold min-w-[20px]">3.</span>
                <span>Choose date range and media quality</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold min-w-[20px]">4.</span>
                <span>Request download (Facebook will email you when ready)</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold min-w-[20px]">5.</span>
                <span>Download the ZIP file</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold min-w-[20px]">6.</span>
                <span>Use "Upload Zip" button in the album to import all photos at once</span>
              </li>
            </ol>
          </div>

          {/* Tips */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="font-semibold mb-2 text-amber-900">💡 Pro Tips</h4>
            <ul className="text-sm space-y-2 text-amber-900">
              <li>• Use the AI Settings (✨ wand icon) to customize how photos are analyzed</li>
              <li>• Apply filters when creating albums to auto-organize photos by date, location, or people</li>
              <li>• The AI will automatically detect dates, locations, and people in your photos</li>
              <li>• For best results with Facebook photos, download your data for complete metadata</li>
            </ul>
          </div>

          {/* Example URL */}
          <div className="bg-gray-50 border rounded-lg p-4">
            <h4 className="font-semibold mb-2">Example Facebook Photo URL:</h4>
            <div className="flex items-center gap-2 bg-white p-2 rounded border font-mono text-xs break-all">
              <code className="flex-1">https://scontent.fxyz1-1.fna.fbcdn.net/v/t1.6435-9/12345_...</code>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => copyText('https://scontent.fxyz1-1.fna.fbcdn.net/v/t1.6435-9/')}
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FacebookImportGuide;
